/* eslint-disable */

import React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { fetchGraphQL } from "../../../services/api-raindrop";
import RaindropLoadingElement from "../../../ui-components/raindrop-loading-element/raindrop-loading-element";

import RaindropButton, {
  ButtonStyle,
} from "../../../ui-components/raindrop-button/raindrop-button.tsx";
import RaindropCircle from "../../../ui-components/raindrop-circle/raindrop-circle.tsx";

import RaindropCarousel from "../../../ui-components/raindrop-carousel/raindrop-carousel";
import TagElementMotion from "../../../ui-components/tag-element-motion/tag-element-motion.tsx";
import OrganisationActivityElement from "../../../ui-components/organisation-activity-element/organisation-activity-element.tsx";
import RaindropMotionFadeUp from "../../../ui-components/raindrop-motion-fade-up/raindrop-motion-fade-up.tsx";
import OrganisationLogo from "../../../ui-components/organisation-logo/organisation-logo.js";
import OrganisationTitle from "../../../ui-components/organisation-title/organisation-title.tsx";
import SupportButton from "../../../ui-components/organisation-support-button/organisation-support-button.js";
import RaindropCard, {
  RaindropCardStyle,
} from "../../../ui-components/raindrop-card/raindrop-card.tsx";
import RaindropHeading from "../../../ui-components/raindrop-heading/raindrop-heading.tsx";

import { Size } from "../../../logic/enums.ts";
import RaindropTextParagraph from "../../../ui-components/raindrop-text-paragraph/raindrop-text-paragraph.tsx";
import RaindropTextHeading1 from "../../../ui-components/raindrop-text-heading-1/raindrop-text-heading-1.tsx";
import RaindropTextSubtitle from "../../../ui-components/raindrop-text-subtitle/raindrop-text-subtitle.tsx";

import ContributionSlider from "../../../ui-components/contribution-slider/contribution-slider.tsx";
import RaindropBackButtonPanel from "../../../ui-components/raindrop-back-button-panel/raindrop-back-button-panel.tsx";
import RaindropClickable from "../../../ui-components/raindrop-clickable/raindrop-clickable.tsx";
import PoliticalChart from "../../../ui-components/political-chart/political-chart.tsx";

import RaindropCardWarning from "../../../ui-components/raindrop-card-warning/raindrop-card-warning.tsx";
import FlexRow from "../../../ui-components/flex-row/flex-row.tsx";
import Spacer from "../../../ui-components/spacer/spacer.js";

import RaindropPage from "../../../ui-components/raindrop-page/raindrop-page.tsx";

const OrganisationDetailMediaComponent = ({ caption, presignedUrl }) => {
  return (
    <div className="flex flex-col gap-4 items-center justify-center h-full w-full">
      <img
        src={presignedUrl}
        className="w-full h-80 object-cover rounded-lg"
        alt=""
      />

      <h4 className="text-color-dark-text-secondary text-sm px-4">{caption}</h4>
    </div>
  );
};

const ContentImages = ({ detailMediaObjects = [] }) => {
  if (detailMediaObjects.length === 0) {
    return null;
  }

  const components = detailMediaObjects.map((dmo) => (
    <OrganisationDetailMediaComponent
      presignedUrl={dmo.media.presignedUrl}
      caption={dmo.caption}
    />
  ));

  return (
    <RaindropMotionFadeUp>
      <div className="flex flex-col w-full ">
        <RaindropCarousel
          components={components}
          pixelWidth={400}
          pixelHeight={350}
        />
      </div>
    </RaindropMotionFadeUp>
  );
};

const ContentLegalInfo = ({ charityNumber, jurisdiction }) => {
  if (!charityNumber || !jurisdiction) {
    return null;
  }

  const InfoRow = ({ label, value }) => {
    return (
      <FlexRow>
        <RaindropTextHeading1>{label}</RaindropTextHeading1>
        <Spacer />
        <RaindropTextSubtitle>{value}</RaindropTextSubtitle>
      </FlexRow>
    );
  };

  let charityNumberLabel = "Charity No.";
  let jurisdictionValue = jurisdiction;

  if (jurisdiction === "GB") {
    charityNumberLabel = "Charity No.";
    jurisdictionValue = <p className="text-4xl">🇬🇧</p>;
  } else if ((jurisdiction = "AU")) {
    charityNumberLabel = "ABN.";
    jurisdictionValue = <p className="text-4xl">🇦🇺</p>;
  }

  return (
    <RaindropCard>
      <RaindropHeading title="Legal Info" isSecondary={true} />
      <InfoRow label={charityNumberLabel} value={charityNumber} />
      <InfoRow label={"Country"} value={jurisdictionValue} />
    </RaindropCard>
  );
};

const ContentTags = ({ tags }) => {
  if (tags.length === 0) {
    return null;
  }

  return (
    <RaindropMotionFadeUp>
      <div className="flex flex-row w-full gap-2 overflow-x-auto no-scrollbar min-h-[40px] ">
        {tags.map((tag) => (
          <TagElementMotion key={tag} value={tag} />
        ))}
      </div>
    </RaindropMotionFadeUp>
  );
};

const ContentLongDescription = ({ descriptionLong }) => {
  if (!descriptionLong) {
    return (
      <RaindropCard>
        <p className="flex w-full text-center items-center text-color-dark-text-tertiary">
          No description provided
        </p>
      </RaindropCard>
    );
  }
  return (
    <RaindropMotionFadeUp>
      <RaindropCard>
        <RaindropHeading title="Description" style={ButtonStyle.Secondary} />

        <div className="flex flex-col w-full gap-4 text-left">
          {descriptionLong.split("\n\n").map((paragraph, index) => (
            <RaindropTextParagraph key={index}>
              {paragraph}
            </RaindropTextParagraph>
          ))}
        </div>
      </RaindropCard>
    </RaindropMotionFadeUp>
  );
};

const ContentOrganisationActivity = ({
  presignedUrl,
  organisationName,
  activityObjects = [],
}) => {
  if (activityObjects.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col w-full gap-6 pt-10">
      <div className="flex flex-col text-left gap-2">
        <h1 className="font-bold text-2xl">Recent activity</h1>
        <p className=" text-color-dark-text-secondary">
          Charities can post activity to show you where your money is going
        </p>
      </div>

      <div className="flex flex-col gap-12">
        {activityObjects.map((activity) => (
          <OrganisationActivityElement
            key={activity.id}
            text={activity.heading}
            organisationPresignedUrl={presignedUrl}
            organisationName={organisationName}
            externalUrl={activity.externalUrl}
            createdAt={activity.createdAt}
          />
        ))}
      </div>
    </div>
  );
};

const ContentRequestAdditionalInfo = ({
  isHydrated,
  organisationPublicEmail,
  namePreferred,
}) => {
  const navigate = useNavigate();
  if (!organisationPublicEmail) {
    return null;
  }

  if (!namePreferred) {
    return (
      <RaindropClickable
        onClick={() => {
          navigate("/raindrop/sign-up");
        }}
      >
        <RaindropCard>
          <RaindropTextParagraph>
            You can request more info by{" "}
            <span className="font-black text-color-raindrop-primary">
              signing up.
            </span>
          </RaindropTextParagraph>
        </RaindropCard>
      </RaindropClickable>
    );
  }

  if (isHydrated) {
    return null;
  }

  function generateMailtoLink(email, subject, body) {
    // Encode the subject and body to ensure special characters are handled
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    // Create the mailto link
    const mailtoLink = `mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`;

    return mailtoLink;
  }

  const subject = "Support via Raindrop App";
  const body = `
  Hello,
  
  I’d love to support you through the Raindrop app, but I’d appreciate having a bit more information first.
  
  In the meantime, you can reach out to the team directly by visiting:
  https://www.theraindropcompany.com
  
  Warm regards,
  ${namePreferred}
  `;
  const mailtoLink = generateMailtoLink(organisationPublicEmail, subject, body);

  return (
    <RaindropCardWarning
      onPrimaryAction={() => {
        window.location.href = mailtoLink;
      }}
      title="Request more info"
      body="Click here to request more information from the charity."
      primaryLabel={"Request info"}
    />
  );
};

const OrganisationHeader = ({
  organisation,
  isSupporting,
  onAddNewOrganisation,
  onCancelOrganisation,
}) => {
  return (
    <div className="flex flex-row gap-4 w-full items-center">
      <OrganisationLogo src={organisation.presignedUrlLogo} isHuge={true} />

      <div className="flex flex-col gap-2 items-start justify-center">
        <OrganisationTitle
          title={organisation.name}
          isHydrated={organisation.isHydrated}
          isLarge={true}
          isShowingLabel={true}
        />
        <SupportButton
          isSupporting={isSupporting}
          onClick={() => onAddNewOrganisation({ organisation: organisation })}
          onCancel={() => onCancelOrganisation({ organisation: organisation })}
        />
      </div>
    </div>
  );
};

const OrganisationInformationComponents = ({
  organisation,
  onAddNewOrganisation,
  onCancelOrganisation,
  isSupporting,
  sausageValues,
  setValues,
  relevantValue,
  userCurrent,
  setCurrentModalComponent,
}) => {
  if (!organisation) {
    return <RaindropLoadingElement isShnoodle={true} />;
  }

  const isPolitical = false;
  return (
    <RaindropMotionFadeUp key={organisation.id} className="w-full">
      <div
        id={organisation.id}
        className="flex flex-col h-full w-full gap-6 no-scrollbar"
      >
        <OrganisationHeader
          organisation={organisation}
          onAddNewOrganisation={onAddNewOrganisation}
          onCancelOrganisation={onCancelOrganisation}
          isSupporting={isSupporting}
        />

        <ContributionSlider
          sausageValue={relevantValue}
          setValues={setValues}
          sausageValues={sausageValues}
        />

        <PoliticalChart
          isPolitical={isPolitical}
          setCurrentModalComponent={setCurrentModalComponent}
        />

        <ContentImages
          detailMediaObjects={organisation.organisationDetailMediaObjects}
        />

        <ContentLongDescription
          descriptionLong={organisation.descriptionLong}
        />

        <ContentLegalInfo
          charityNumber={organisation.identifier?.identifierValue}
          jurisdiction={organisation.identifier?.jurisdiction}
        />

        <ContentTags tags={organisation.tags} />

        <ContentOrganisationActivity
          activityObjects={organisation.activityObjects}
          presignedUrl={organisation.presignedUrlLogo}
          organisationName={organisation.name}
        />
        <ContentRequestAdditionalInfo
          isHydrated={organisation.isHydrated}
          organisationPublicEmail={organisation.publicEmail}
          namePreferred={userCurrent?.namePreferred}
        />
      </div>
    </RaindropMotionFadeUp>
  );
};

const RaindropOrganisation = ({
  sausageValues,
  setValues,
  onAddNewOrganisation,
  onCancelOrganisation,
  userCurrent,
  setCurrentModalComponent,
}) => {
  const navigate = useNavigate();

  const { organisationId } = useParams();

  const [organisation, setOrganisation] = useState(null);

  const [isError, setIsError] = useState(false);

  const [errorRetries, setErrorRetries] = useState(0);

  const relevantValue = sausageValues?.find(
    (v) => v.organisation?.id === organisationId
  );

  const isSupporting = relevantValue !== undefined;

  useEffect(() => {
    fetchGraphQL(`
      query {
        organisation(id: "${organisationId}") {
          id
          name
          publicEmail
          presignedUrlLogo
          descriptionShort
          descriptionLong
          isHydrated
          tags
          identifier {
            id
            jurisdiction
            identifierValue
          }
          organisationDetailMediaObjects {
            id
            caption
            media {
              id
              presignedUrl
            }
          }
          activityObjects {
            id
            heading
            externalUrl
            createdAt
            organisation {
              presignedUrlLogo
              name
            }
          }
        }
      }
    `)
      .then((result) => {
        if (result.errors) {
          console.error(result.errors);
          setIsError(true);
          return;
        }
        setOrganisation(result.data.organisation);
      })
      .catch(() => {
        setIsError(true);
      });
  }, [organisationId]);

  // Highlight the current waterfall in the sausage
  useEffect(() => {
    if (!sausageValues) {
      return;
    }
    const newValues = sausageValues.map((v) => {
      return {
        ...v,
        selected: v.organisation && v.organisation?.id === organisationId,
      };
    });

    setValues(newValues);

    return () => {
      const newValues = sausageValues.map((v) => {
        return {
          ...v,
          selected: false,
        };
      });

      setValues(newValues);
    };
  }, [organisation]);

  if (isError) {
    return (
      <div className="flex flex-col h-full items-center justify-center max-w-sm gap-4">
        <p className="text-color-dark-text-secondary">
          Something has gone wrong. Check your internet connection and try
          again.
        </p>
        <RaindropButton
          onClick={() => setErrorRetries(errorRetries + 1)}
          title="Retry"
          icon="refresh"
        />
      </div>
    );
  }

  return (
    <RaindropPage>
      <RaindropBackButtonPanel />

      <div className="flex xl:hidden">
        <RaindropCircle
          sausageValues={sausageValues}
          size={"XSMALL"}
          isRaindropPlus={userCurrent?.account?.isRaindropPlus}
          onClick={() => {
            navigate("/raindrop");
          }}
          isSpringyTransition={false}
          isPaused={userCurrent?.account?.accountPlan?.isPaused}
          accountAwardFeaturedSlug={
            userCurrent?.account?.accountAwardFeatured?.award?.slug
          }
        />
      </div>

      <OrganisationInformationComponents
        organisation={organisation}
        onAddNewOrganisation={onAddNewOrganisation}
        onCancelOrganisation={onCancelOrganisation}
        isSupporting={isSupporting}
        sausageValues={sausageValues}
        setValues={setValues}
        relevantValue={relevantValue}
        userCurrent={userCurrent}
        setCurrentModalComponent={setCurrentModalComponent}
      />
    </RaindropPage>
  );
};

export default RaindropOrganisation;
